// PipelineTab — extracted from ProjectsPage.jsx ({tab === 'pipeline' && ...}
// block, lines ~1568–1650 in the legacy file). Move, not rewrite: same
// table shape, same dropped-projects details, same click-to-detail
// navigation, same premium gating.
//
// Dependencies duplicated locally so this file has zero coupling to
// ProjectsPage.jsx (which is scheduled for deletion once parity is signed
// off): STAGES + daysSince + thumbUrl + a tiny score→pct helper. Ownership
// mutations reuse the ProjectsContext.
//
// scoreColor was a proxy to tierForIndex(pct).color in ProjectsPage —
// use tierForIndex directly here.

import React from 'react';
import { useProjects } from '../../contexts/ProjectsContext';
import { useIsPremium } from '../../contexts/PremiumContext';
import { LockedCard } from '../PremiumLock/PremiumLock';
import { tierForIndex } from '../SeidrIndex';

// Copies of small helpers from ProjectsPage.jsx. When that file is deleted
// they stay here; when both files exist during the parity window they're
// duplicated (intentionally — avoids reaching into the legacy file).
const STAGES = [
  { key: 'candidate', label: 'Candidate' },
  { key: 'screening', label: 'Screening' },
  { key: 'land_contact', label: 'Land Contact' },
  { key: 'option_signed', label: 'Option Signed' },
  { key: 'grid_applied', label: 'Grid Applied' },
];
const MAPBOX_TOKEN = import.meta.env?.VITE_MAPBOX_TOKEN || '';
const thumbUrl = (lat, lon) =>
  `https://api.mapbox.com/styles/v1/mapbox/satellite-v9/static/pin-s+ef4444(${lon},${lat})/${lon},${lat},13,0/200x100@2x?access_token=${MAPBOX_TOKEN}`;
function daysSince(iso) {
  if (!iso) return '';
  const d = Math.floor((Date.now() - new Date(iso)) / 86400000);
  return d === 0 ? 'today' : `${d}d ago`;
}
const pctOf = (p) => Math.round(Number(p.readiness_score) || 0);

export default function PipelineTab() {
  const { projects, activeProjects, updateProject } = useProjects();
  const isPremium = useIsPremium();

  if (!isPremium) {
    return (
      <LockedCard
        title="Pipeline — Premium"
        body="Manage your active project pipeline (land options, grid applications, permitting, investment) in one table."
      />
    );
  }

  if (!activeProjects || activeProjects.length === 0) {
    return (
      <div className="pp-empty">
        <div className="pp-empty-title">No projects yet</div>
        <div className="pp-empty-text">Save sites from the map to start tracking them.</div>
      </div>
    );
  }

  const handleStageChange = (project, newStage) => {
    updateProject?.(project.id, { stage: newStage });
  };

  const dropped = (projects || []).filter(p => p.stage === 'dropped');

  return (
    <div className="pp-pipeline">
      <table className="pp-pipeline-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Area</th>
            <th>Grid</th>
            <th>Score</th>
            <th>Stage</th>
            <th>Updated</th>
          </tr>
        </thead>
        <tbody>
          {activeProjects.map(p => {
            const e = p.enrichment || {};
            const pct = e.readiness_score != null ? pctOf(p) : null;
            const color = pct != null ? tierForIndex(pct).color : '#94a3b8';
            return (
              <tr key={p.id} className="pp-pipeline-row"
                  onClick={() => { window.location.hash = `#/projects/${p.id}`; }}>
                <td className="pp-pl-name">
                  <img src={thumbUrl(p.lat, p.lon)} className="pp-pl-thumb" alt=""
                       onError={ev => { ev.target.style.display = 'none'; }} />
                  <span>{p.site_label}</span>
                </td>
                <td>{(e.total_area_ha || e.areal_ha) ? `${Number(e.total_area_ha || e.areal_ha).toFixed(0)} ha` : '—'}</td>
                <td>{e.nearest_grid_voltage_kv ? `${e.nearest_grid_voltage_kv} kV · ${(e.nearest_grid_distance_km || 0).toFixed(1)} km` : '—'}</td>
                <td style={{ color, fontWeight: pct != null ? 700 : 400, fontSize: pct != null ? undefined : 11 }}>
                  {pct ?? 'pending'}
                </td>
                <td onClick={ev => ev.stopPropagation()}>
                  <select className="pp-pl-stage" value={p.stage}
                          onChange={ev => handleStageChange(p, ev.target.value)}>
                    {STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                    <option value="dropped">Drop</option>
                  </select>
                </td>
                <td className="pp-pl-updated">{daysSince(p.updated_at)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {dropped.length > 0 && (
        <details className="pp-dropped">
          <summary>Dropped ({dropped.length})</summary>
          {dropped.map(p => (
            <div key={p.id} className="pp-dropped-item">
              <span>{p.site_label}</span>
              <button onClick={() => updateProject(p.id, { stage: 'candidate' })}>Restore</button>
            </div>
          ))}
        </details>
      )}
    </div>
  );
}
